import React, { useState, useRef } from 'react';
import '../css/textAreaInline.css';

export interface TextAreaInlineProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  rows?: number;
  height?: number;
  maxLength?: number;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
  warningText?: string;
}

const LINE_HEIGHT = 22;
const PADDING_V = 18;
const FOOTER_H = 22;

const TextAreaInline: React.FC<TextAreaInlineProps> = ({
  id,
  name,
  value = '',
  placeholder,
  onChange,
  onFocus,
  onBlur,
  required = false,
  disabled = false,
  className = '',
  rows = 3,
  height,
  maxLength,
  assistiveText,
  error = false,
  errorText,
  warning = false,
  warningText,
}) => { //NOSONAR
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isAtLimit = maxLength !== undefined && value.length >= maxLength;

  const footerExtra = maxLength !== undefined ? FOOTER_H : 0; //NOSONAR
  const innerH = height ?? rows * LINE_HEIGHT;
  const plateMinH = innerH + PADDING_V + footerExtra;

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => { setIsFocused(true); onFocus?.(e); };
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => { setIsFocused(false); onBlur?.(e); };
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange?.(e.target.value);
  const handleClick = () => textareaRef.current?.focus();

  const assistiveMessage =
    error && errorText ? errorText :
      warning && warningText ? warningText : //NOSONAR
        assistiveText;

  const assistiveClass =
    error && errorText ? 'scl--cms-components-textAreaInline-assistive--error' :
      warning && warningText ? 'scl--cms-components-textAreaInline-assistive--warning' : //NOSONAR
        '';

  const plateClasses = [
    'scl--cms-components-textAreaInline-plate',
    isFocused ? 'scl--cms-components-textAreaInline--focused' : '',
    error ? 'scl--cms-components-textAreaInline--error' : '',
    warning && !error ? 'scl--cms-components-textAreaInline--warning' : '',
    disabled ? 'scl--cms-components-textAreaInline--disabled' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`scl--cms-components-textAreaInline-container ${className}`}>
      <div //NOSONAR
        className={plateClasses}
        style={{ minHeight: `${plateMinH}px` }}
        onClick={handleClick}
      >
        <div className="scl--cms-components-textAreaInline-wrapper">
          <textarea
            ref={textareaRef}
            id={id}
            name={name}
            value={value}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{ height: `${innerH}px` }}
            className="scl--cms-components-textAreaInline-input"
            autoComplete="off"
          />
        </div>

        {maxLength !== undefined && (
          <div className="scl--cms-components-textAreaInline-footer">
            <span
              className={`scl--cms-components-textAreaInline-charCount ${isAtLimit ? 'scl--cms-components-textAreaInline-charCount--limit' : ''
                }`}
            >
              {value.length}/{maxLength}
            </span>
          </div>
        )}
      </div>

      {assistiveMessage && (
        <div className={`scl--cms-components-textAreaInline-assistive ${assistiveClass}`}>
          {assistiveMessage}
        </div>
      )}
    </div>
  );
};

export default TextAreaInline;